import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useUser } from './useUser';

/**
 * Hook pour vérifier si l'utilisateur a les permissions requises
 * @param requiredRoles - Rôles requis pour accéder à la page
 * @param redirectTo - Page vers laquelle rediriger si l'utilisateur n'a pas les permissions (par défaut: /admin/login)
 */
export const useCheckPermissions = (
  requiredRoles: string[] = ['admin'],
  redirectTo: string = '/admin/login'
) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { user, isLoading } = useUser();

  useEffect(() => {
    const checkPermissions = async () => {
      // Si on est encore en train de charger les données utilisateur, on attend
      if (isLoading) return;

      // Si l'utilisateur n'est pas authentifié, on le redirige
      if (!isAuthenticated) {
        navigate(redirectTo);
        return;
      }

      // Si l'utilisateur est authentifié mais qu'on n'a pas ses données, c'est une erreur
      if (!user) {
        navigate(redirectTo);
        return;
      }

      // Vérifier si l'utilisateur a un des rôles requis
      const hasRequiredRole = requiredRoles.includes(user.role);
      
      // Si l'utilisateur n'a pas les permissions requises, on le redirige
      if (!hasRequiredRole) {
        navigate(redirectTo);
      }
    };

    checkPermissions();
  }, [isAuthenticated, user, isLoading, navigate, redirectTo, requiredRoles]);

  return { user, isLoading };
};

export default useCheckPermissions;
